import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import VisaFormField from './VisaFormField';
import {
  parseVisaFormResponse,
  flattenFormSections,
  buildFormFieldLabels,
} from '../utils/visaFormHelpers';
import {
  getApplicationFormParams,
  getInitialFormValues,
  isFormFilled,
  isCompleteFormParams,
} from '../utils/visaApplicationData';
import { getVisaFormParamsUrl, getVisaFormUpdateUrl } from '../utils/visaApplicationHistoryApi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const freshRequestConfig = {
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
};

const VisaApplicationFormModal = ({ application, onClose, onSaved }) => {
  const [formDefinition, setFormDefinition] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const parsedForm = useMemo(() => parseVisaFormResponse(formDefinition), [formDefinition]);
  const formFields = useMemo(() => flattenFormSections(parsedForm.sections), [parsedForm.sections]);
  const alreadyFilled = isFormFilled(application?.customer_visa_application_data);

  useEffect(() => {
    const loadForm = async () => {
      setLoading(true);
      setError('');

      let formParams = getApplicationFormParams(application);
      let paramsError = '';

      if (!isCompleteFormParams(formParams)) {
        try {
          const paramsRes = await axios.get(getVisaFormParamsUrl(application.id), freshRequestConfig);
          formParams = paramsRes.data?.form_params || formParams;
        } catch (err) {
          paramsError = err.response?.data?.error || 'Could not resolve visa form parameters';
        }
      }

      if (!isCompleteFormParams(formParams)) {
        setError(
          paramsError ||
            'Unable to load form for this application. Missing visa form metadata.'
        );
        setLoading(false);
        return;
      }

      try {
        const formRes = await axios.post(`${API_URL}/visa-apply/form`, formParams, freshRequestConfig);
        const parsed = parseVisaFormResponse(formRes.data);
        if (!parsed.sections.length) {
          setError('No form fields returned for this visa application');
          setLoading(false);
          return;
        }

        setFormDefinition(formRes.data);
        setFormValues(getInitialFormValues(application));
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load visa application form');
      } finally {
        setLoading(false);
      }
    };

    if (application) {
      loadForm();
    }
  }, [application]);

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = formFields.filter((field) => field.required && !formValues[field.name]);
    if (missing.length > 0) {
      setError(`Please fill required fields: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axios.put(getVisaFormUpdateUrl(application.id), {
        customer_visa_application_data: {
          ...formValues,
          visa_name: parsedForm.meta?.visaName || application.visa_type,
          form_sections: parsedForm.sections.map((section) => section.title),
          form_field_labels: buildFormFieldLabels(parsedForm.sections),
        },
      });

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save visa application form');
    } finally {
      setSubmitting(false);
    }
  };

  if (!application) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {alreadyFilled ? 'Update Visa Application Form' : 'Complete Visa Application Form'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {application.visa_type} — {application.from_country_name || application.from_country} to{' '}
              {application.to_country_name || application.to_country}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500 py-8 text-center">Loading visa form...</p>
        ) : formFields.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">No form fields available.</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {parsedForm.meta?.notes?.length > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-900 space-y-2">
                {parsedForm.meta.notes.map((note, index) => (
                  <p key={index}>{note}</p>
                ))}
              </div>
            )}

            {parsedForm.sections.map((section) => (
              <div key={section.title} className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-100 pb-2">
                  {section.title}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {section.fields.map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                        {field.required ? ' *' : ''}
                      </label>
                      <VisaFormField
                        field={field}
                        value={formValues[field.name]}
                        onChange={handleFieldChange}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary disabled:opacity-50"
              >
                {submitting ? 'Saving...' : alreadyFilled ? 'Update Form' : 'Submit Form'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default VisaApplicationFormModal;
